import React, { useEffect, useState, useCallback } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  CircularProgress,
  Button,
  Tooltip,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import { green } from "@material-ui/core/colors";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { fmtDateTimeShort } from "../../translate/localeHelper";

// Paleta de facto (igual que Connections/index.js + MetaHealthBanner).
const SEMAPHORE = {
  green: green[500],
  yellow: "#f59e0b",
  red: "#ef4444",
  none: "#9ca3af",
};

const HEALTH_COLOR = {
  ok: green[500],
  error: "#ef4444",
  warning: "#f59e0b",
  neutral: "#9ca3af",
};

const QUALITY_COLOR = { GREEN: green[500], YELLOW: "#f59e0b", RED: "#ef4444" };

// Pedido de historial de Coexistence (Whatsapps.historySyncStatus).
const HISTORY_SYNC_COLOR = {
  success: green[500],
  pending: "#f59e0b",
  failed: "#ef4444",
  expired: "#ef4444",
  not_shared: "#ef4444",
  skipped: "#9ca3af",
};

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3), maxWidth: 1100 },
  paper: { padding: theme.spacing(2), marginTop: theme.spacing(2), marginBottom: theme.spacing(3) },
  help: { color: theme.palette.text.secondary, fontSize: 13, marginTop: 4 },
  sectionHead: { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: theme.spacing(1) },
  sectionTitle: { fontWeight: 700 },
  dot: { display: "inline-block", width: 12, height: 12, borderRadius: "50%", marginRight: 8, verticalAlign: "middle" },
  statusChip: { color: "#fff", fontWeight: 700, height: 22 },
  mono: { fontFamily: "monospace", fontSize: 12, color: theme.palette.text.secondary, wordBreak: "break-all" },
  muted: { color: theme.palette.text.secondary },
  empty: { padding: theme.spacing(3), textAlign: "center", color: theme.palette.text.secondary },
}));

const SuperAdminConnections = () => {
  const classes = useStyles();
  const [overview, setOverview] = useState([]);
  const [metaLines, setMetaLines] = useState([]);
  const [loading, setLoading] = useState(true);

  const t = (k, opts) => i18n.t(`superAdminConnections.${k}`, opts);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, meta] = await Promise.all([
        api.get("/superadmin/connections-overview"),
        api.get("/superadmin/meta-overview"),
      ]);
      setOverview(ov.data.companies || []);
      setMetaLines(meta.data.lines || []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const semaphoreChip = (s) => (
    <Chip
      size="small"
      className={classes.statusChip}
      style={{ background: SEMAPHORE[s] || SEMAPHORE.none }}
      label={t(`semaphore.${s}`)}
    />
  );

  const healthCell = (health) => {
    if (!health || !health.code) return <span className={classes.muted}>—</span>;
    const level = health.level || "warning";
    const color =
      level === "ok" ? HEALTH_COLOR.ok
        : level === "error" ? HEALTH_COLOR.error
          : health.code === "check_failed" ? HEALTH_COLOR.neutral
            : HEALTH_COLOR.warning;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <span className={classes.dot} style={{ background: color, marginRight: 0 }} />
        <span style={{ fontSize: 13 }}>
          {i18n.t(`connections.metaHealth.${health.code}.title`)}
        </span>
        {health.code === "quality_low" && health.qualityRating && (
          <Chip
            size="small"
            className={classes.statusChip}
            style={{ background: QUALITY_COLOR[health.qualityRating] || SEMAPHORE.none }}
            label={i18n.t(`connections.metaHealth.quality.${health.qualityRating}`)}
          />
        )}
      </div>
    );
  };

  const historySyncCell = (hs) => {
    if (!hs) return <span className={classes.muted}>—</span>;
    const lines = [
      t("historySync.attempts", { count: hs.attempts }),
      hs.requestedAt && t("historySync.requestedAt", { date: fmtDateTimeShort(hs.requestedAt) }),
      hs.status === "pending" && hs.nextAt && t("historySync.nextAt", { date: fmtDateTimeShort(hs.nextAt) }),
      hs.status === "not_shared" && hs.notSharedAt && t("historySync.notSharedAt", { date: fmtDateTimeShort(hs.notSharedAt) }),
      hs.lastError && t("historySync.lastError", { error: hs.lastError }),
    ].filter(Boolean);
    return (
      <Tooltip title={<div style={{ whiteSpace: "pre-line" }}>{lines.join("\n")}</div>}>
        <Chip
          size="small"
          className={classes.statusChip}
          style={{ background: HISTORY_SYNC_COLOR[hs.status] || SEMAPHORE.none }}
          label={t(`historySync.status.${hs.status}`, { defaultValue: hs.status })}
        />
      </Tooltip>
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.sectionHead}>
        <div>
          <Typography variant="h5">{t("title")}</Typography>
          <Typography className={classes.help}>{t("subtitle")}</Typography>
        </div>
        <Tooltip title={t("refresh")}>
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
              disabled={loading}
              onClick={fetchAll}
            >
              {t("refresh")}
            </Button>
          </span>
        </Tooltip>
      </div>

      {/* ── Semáforo por empresa ── */}
      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>{t("semaphoreSection")}</Typography>
        <Typography className={classes.help}>{t("semaphoreHelp")}</Typography>
        {loading && overview.length === 0 ? (
          <div className={classes.empty}><CircularProgress size={28} /></div>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("company")}</TableCell>
                <TableCell align="center">{t("state")}</TableCell>
                <TableCell align="center">{t("total")}</TableCell>
                <TableCell align="center">{t("connected")}</TableCell>
                <TableCell align="center">{t("transitional")}</TableCell>
                <TableCell align="center">{t("down")}</TableCell>
                <TableCell>{t("lastChange")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {overview.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.name} <span className={classes.muted}>(ID {c.id})</span></TableCell>
                  <TableCell align="center">{semaphoreChip(c.semaphore)}</TableCell>
                  <TableCell align="center">{c.total}</TableCell>
                  <TableCell align="center" style={{ color: c.connected > 0 ? SEMAPHORE.green : undefined }}>{c.connected}</TableCell>
                  <TableCell align="center" style={{ color: c.transitional > 0 ? SEMAPHORE.yellow : undefined }}>{c.transitional}</TableCell>
                  <TableCell align="center" style={{ color: c.down > 0 ? SEMAPHORE.red : undefined }}>{c.down}</TableCell>
                  <TableCell className={classes.muted}>{c.lastStatusChangeAt ? fmtDateTimeShort(c.lastStatusChangeAt) : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* ── Líneas Meta ── */}
      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>{t("metaSection")}</Typography>
        <Typography className={classes.help}>{t("metaHelp")}</Typography>
        {loading && metaLines.length === 0 ? (
          <div className={classes.empty}><CircularProgress size={28} /></div>
        ) : metaLines.length === 0 ? (
          <div className={classes.empty}>{t("metaEmpty")}</div>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("company")}</TableCell>
                <TableCell>{t("line")}</TableCell>
                <TableCell>{t("type")}</TableCell>
                <TableCell>{t("phoneNumberId")}</TableCell>
                <TableCell>{t("wabaId")}</TableCell>
                <TableCell align="center">{t("status")}</TableCell>
                <TableCell>{t("health")}</TableCell>
                <TableCell>{t("historySync.title")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {metaLines.map((l) => (
                <TableRow key={l.whatsapp.id}>
                  <TableCell>{l.companyName} <span className={classes.muted}>(ID {l.companyId})</span></TableCell>
                  <TableCell>{l.whatsapp.name}<br /><span className={classes.mono}>{l.whatsapp.number || "—"}</span></TableCell>
                  <TableCell>{l.whatsapp.provider === "meta_coexistence" ? t("coexistence") : "Cloud API"}</TableCell>
                  <TableCell className={classes.mono}>{l.whatsapp.metaPhoneNumberId || "—"}</TableCell>
                  <TableCell className={classes.mono}>{l.whatsapp.metaWabaId || "—"}</TableCell>
                  <TableCell align="center">
                    <span className={classes.dot} style={{ background: l.whatsapp.status === "CONNECTED" ? SEMAPHORE.green : SEMAPHORE.red, marginRight: 0 }} />
                  </TableCell>
                  <TableCell>{healthCell(l.health)}</TableCell>
                  <TableCell>{historySyncCell(l.historySync)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </div>
  );
};

export default SuperAdminConnections;
